/**
 * 화면 위 가운데 토스트 — 한 벌 (v2.317.0 · Salesforce 식).
 *
 *   - 종류 넷: ok(초록) · error(빨강) · warning(주황·어두운 글자) · info(회색). 아이콘 + 글자 + 색이 늘 함께 간다.
 *   - 5초 뒤 저절로 사라지고 × 로 바로 닫는다. 동시에 최대 3개, 새것이 위에.
 *   - React 밖 DOM — 창이 닫히며 결과 띠가 사라지던 자리(저장 뒤 목록으로 돌아올 때)에서 쓴다.
 *     창 **안**에서 바로 보는 결과 띠는 그대로 둔다(자리 맥락이 있다).
 *   - 값은 반드시 textContent 로만 넣는다(사용자 데이터가 HTML 로 해석되지 않게).
 *   - z-[120] — 전역 모달(z-90/95)보다 위.
 */

export type ToastTone = 'ok' | 'error' | 'warning' | 'info';

const TOAST_MS = 5000;
const MAX_STACK = 3;

let host: HTMLDivElement | null = null;

function ensureHost(): HTMLDivElement {
  if (host && host.isConnected) return host;
  host = document.createElement('div');
  host.className =
    'fixed top-4 left-1/2 -translate-x-1/2 z-[120] pointer-events-none px-4 max-w-[92vw] flex flex-col items-center gap-2';
  document.body.appendChild(host);
  return host;
}

const TONE: Record<ToastTone, { cls: string; icon: string; label: string }> = {
  ok: { cls: 'bg-emerald-600 text-white', icon: '✓', label: '성공' },
  error: { cls: 'bg-red-500 text-white', icon: '✕', label: '오류' },
  warning: { cls: 'bg-amber-400 text-slate-900', icon: '⚠', label: '주의' },
  info: { cls: 'bg-slate-500 text-white', icon: 'ⓘ', label: '안내' },
};

export function notify(
  tone: ToastTone,
  text: string,
  opts: { strong?: string; ms?: number } = {},
): void {
  const el = ensureHost();
  const t = TONE[tone];

  const card = document.createElement('div');
  card.className =
    `pointer-events-auto flex items-start gap-3 rounded min-w-[320px] max-w-[640px] ${t.cls} shadow-md ` +
    'px-4 py-3 text-sm leading-relaxed opacity-0 transition-opacity duration-150';
  card.setAttribute('role', tone === 'error' ? 'alert' : 'status');

  const icon = document.createElement('span');
  icon.className = 'font-bold shrink-0';
  icon.textContent = t.icon;
  icon.setAttribute('aria-label', t.label);
  card.appendChild(icon);

  const body = document.createElement('span');
  body.className = 'break-words flex-1 min-w-0';
  if (opts.strong) {
    const b = document.createElement('b');
    b.className = 'font-semibold';
    b.textContent = opts.strong;
    body.appendChild(b);
    body.appendChild(document.createTextNode(' '));
  }
  body.appendChild(document.createTextNode(text));
  card.appendChild(body);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'shrink-0 -mr-1 -mt-0.5 h-6 w-6 rounded leading-none opacity-80 hover:opacity-100 hover:bg-black/10';
  close.textContent = '×';
  close.setAttribute('aria-label', '닫기');
  card.appendChild(close);

  let timer: ReturnType<typeof setTimeout> | null = null;
  const remove = () => {
    if (timer) clearTimeout(timer);
    card.classList.add('opacity-0');
    setTimeout(() => card.remove(), 160);
  };
  close.addEventListener('click', remove);

  // 새것이 위에 · 넘치면 가장 오래된 것부터 지운다
  el.prepend(card);
  while (el.children.length > MAX_STACK) el.lastElementChild?.remove();

  requestAnimationFrame(() => card.classList.remove('opacity-0'));
  timer = setTimeout(remove, opts.ms ?? TOAST_MS);
}

export const toastOk = (text: string, strong?: string) => notify('ok', text, { strong });
export const toastError = (text: string, strong?: string) => notify('error', text, { strong });
export const toastWarn = (text: string, strong?: string) => notify('warning', text, { strong });
export const toastInfo = (text: string, strong?: string) => notify('info', text, { strong });
